import { useState, type FormEvent } from 'react';
import { api } from '../../shared/api/client';
import type { MinistryDetail, PersonRef } from '../../shared/api/types';
import { Button, Card } from '../../shared/ui/components';
import { personOptions, SearchSelect } from '../../shared/ui/SearchSelect';

type Run = (action: () => Promise<unknown>, success: string) => Promise<void>;

/** Puestos del ministerio (en Alabanza: Batería, Bajo…). La administración, el pastor o el líder los crean y asignan. */
export function PositionsCard({ ministry: m, churchId, run, busy }: { ministry: MinistryDetail; churchId: string; run: Run; busy: boolean }) {
  const [name, setName] = useState('');
  const [variable, setVariable] = useState(false);
  const manage = m.canManagePositions;
  const base = `/organization/churches/${churchId}/positions`;
  // A quién se le puede dar un puesto: integrantes, líder y co-líderes del ministerio.
  const team = new Map<string, PersonRef>();
  for (const p of [...(m.leader ? [m.leader] : []), ...m.coLeaders, ...m.members]) team.set(p.personId, p);
  const people = [...team.values()].sort((a, b) => a.name.localeCompare(b.name, 'es'));

  function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const clean = name.trim();
    if (!clean) return;
    void run(() => api.post(`/organization/churches/${churchId}/ministries/${m.id}/positions`, { name: clean, variable }), `Puesto ${clean} creado.`).then(() => setName(''));
  }

  if (!manage && m.positions.length === 0) return null;
  return (
    <Card title={`Puestos (${m.positions.length})`}>
      <p className="small muted">
        {m.systemKey === 'alabanza' ? 'Batería, bajo, teclado, voces… y quién ocupa cada puesto.' : 'Los puestos del ministerio y quién ocupa cada uno.'}
        {manage ? ' Un puesto puede tener a varias personas.' : ''}
      </p>
      {m.positions.length === 0 ? <p className="muted">Todavía no hay puestos.{manage ? ' Crea el primero abajo.' : ''}</p> : null}
      <ul className="list">
        {m.positions.map((p) => (
          <li key={p.id} className="list__item">
            <div className="service__main">
              <span>
                <strong>{p.name}</strong>{' '}
                {manage ? (
                  <select
                    aria-label={`¿Quién ocupa ${p.name}?`}
                    value={p.variable ? 'variable' : 'permanente'}
                    disabled={busy}
                    onChange={(e) => {
                      const v = e.target.value === 'variable';
                      void run(() => api.patch(`${base}/${p.id}`, { variable: v }), v ? `En ${p.name} ahora se elige quién en cada culto.` : `${p.name} ahora lo ocupan siempre los mismos.`);
                    }}
                  >
                    <option value="permanente">Siempre los mismos</option>
                    <option value="variable">Se elige en cada culto</option>
                  </select>
                ) : (
                  <span className="badge">{p.variable ? 'Se elige en cada culto' : 'Siempre los mismos'}</span>
                )}
              </span>
              <span className="small">
                {p.members.length === 0 ? <span className="muted">Sin asignar</span> : null}
                {manage
                  ? p.members.map((x) => (
                      <span key={x.personId} className="chip-inline">
                        {x.name}
                        <Button
                          variant="ghost"
                          aria-label={`Quitar a ${x.name} de ${p.name}`}
                          disabled={busy}
                          onClick={() => {
                            if (window.confirm(`¿Quitar a ${x.name} del puesto ${p.name}?`)) void run(() => api.delete(`${base}/${p.id}/members/${x.personId}`), `${x.name} ya no está en ${p.name}.`);
                          }}
                        >
                          Quitar
                        </Button>
                      </span>
                    ))
                  : p.members.map((x) => x.name).join(', ')}
              </span>
              {manage ? <AssignForm position={p} people={people.filter((x) => !p.members.some((y) => y.personId === x.personId))} busy={busy} onPick={(personId, who) => void run(() => api.put(`${base}/${p.id}/members/${personId}`, {}), `${who} quedó en ${p.name}.`)} /> : null}
            </div>
            {manage ? (
              <Button
                type="button"
                variant="ghost"
                disabled={busy}
                onClick={() => {
                  if (window.confirm(`¿Borrar el puesto ${p.name}?`)) void run(() => api.delete(`${base}/${p.id}`), `Puesto ${p.name} borrado.`);
                }}
              >
                Borrar
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {manage ? (
        <form className="inline-form inline-form--wrap" onSubmit={create}>
          <label className="visually-hidden" htmlFor="new-position">
            Nombre del nuevo puesto
          </label>
          <input id="new-position" placeholder={m.systemKey === 'alabanza' ? 'Nuevo puesto (ej. Batería, Bajo)…' : 'Nuevo puesto…'} maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
          <label className="visually-hidden" htmlFor="new-position-kind">
            ¿Quién ocupa el puesto?
          </label>
          <select id="new-position-kind" value={variable ? 'variable' : 'permanente'} onChange={(e) => setVariable(e.target.value === 'variable')}>
            <option value="permanente">Siempre los mismos</option>
            <option value="variable">Se elige en cada culto</option>
          </select>
          <Button type="submit" busy={busy} disabled={!name.trim()}>
            + Agregar puesto
          </Button>
        </form>
      ) : null}
    </Card>
  );
}

function AssignForm({ position: p, people, busy, onPick }: { position: { id: string; name: string }; people: PersonRef[]; busy: boolean; onPick: (personId: string, name: string) => void }) {
  if (people.length === 0) return null;
  return (
    <form
      className="inline-form"
      onSubmit={(e) => {
        e.preventDefault();
        const personId = String(new FormData(e.currentTarget).get('personId') ?? '');
        const who = people.find((x) => x.personId === personId);
        if (who) onPick(personId, who.name);
      }}
    >
      <label className="visually-hidden" htmlFor={`pos-${p.id}`}>
        Asignar {p.name}
      </label>
      <SearchSelect id={`pos-${p.id}`} name="personId" placeholder="Asignar a…" options={personOptions(people)} />
      <Button type="submit" variant="secondary" busy={busy}>
        Asignar
      </Button>
    </form>
  );
}
